/**
 * How a failed app-api call reads, one way, for every app that shows it to a person.
 *
 * app-api refuses in the v3 error body: `{"api":"v3","errors":[{status, code, message, reason?}]}`.
 * Its `reason` is what proves the API wrote `message` for a person: a refusal it made on purpose
 * names one, and its absence leaves nothing to say who the sentence is for. Everything else that
 * lands in a catch block is machinery talking to machinery — the framework's bare 500 says
 * "Server Error", ofetch names the HTTP method and the whole API URL, and a route that answers an
 * exception it did not foresee repeats that exception's text.
 */
/**
 * The first entry of a v3 error body, or null when the body is not one: Laravel's field map keys
 * `errors` by field, and the framework's own 500 has no `errors` at all. An `errors` array with no
 * usable first entry is still the envelope, and reads as an entry with neither half.
 */
export function envelopeEntry(body) {
    const errors = body?.errors;
    if (!Array.isArray(errors)) {
        return null;
    }
    const first = errors[0];
    const { message, reason } = (typeof first === 'object' && first !== null ? first : {});
    return { message: nonEmpty(message), reason: nonEmpty(reason) };
}
/** The envelope's reason alone, for a caller that branches on one refusal. */
export function envelopeReason(body) {
    return envelopeEntry(body)?.reason ?? null;
}
/**
 * The fields a v3 error body refuses, so a form marks every one from a single answer: app-api's
 * validation refusal is one entry per broken rule, each naming the `field` it refuses as the
 * request sent it (`custom_headers.0.key` for a list's). An entry counts only with a field, a
 * sentence and a reason, the reason being what proves, as for `memberFacingMessage`, that the
 * sentence was written for a person. Empty when the body is not the envelope or refuses no field.
 */
export function envelopeFieldErrors(body) {
    const errors = body?.errors;
    if (!Array.isArray(errors)) {
        return {};
    }
    // No prototype, so a field the request named `constructor` or `__proto__` is a field like any
    // other rather than a member every object inherits.
    const fields = Object.create(null);
    for (const entry of errors) {
        const { field, message, reason } = (typeof entry === 'object' && entry !== null ? entry : {});
        const name = nonEmpty(field);
        const sentence = nonEmpty(message);
        if (name !== null && sentence !== null && nonEmpty(reason) !== null) {
            (fields[name] ??= []).push(sentence);
        }
    }
    return fields;
}
/** The four fields every entry of the v3 error body carries; anything else on it is the reason's own. */
const ENVELOPE_FIELDS = new Set(['status', 'code', 'message', 'reason']);
/**
 * The first entry's own details beyond `status`, `code`, `message` and `reason` — the fields a
 * reason carries for the caller to act on (`seat_ids`, `version`, `attempts`, `decline_code`, …).
 * Empty when the body is not the envelope or the entry carries none.
 */
export function envelopeDetails(body) {
    const errors = body?.errors;
    const first = Array.isArray(errors) ? errors[0] : undefined;
    if (typeof first !== 'object' || first === null) {
        return {};
    }
    return Object.fromEntries(Object.entries(first).filter(([field]) => !ENVELOPE_FIELDS.has(field)));
}
/**
 * A `Retry-After` header as whole seconds from `now`: the delta-seconds form ("30") or the
 * HTTP-date form ("Wed, 08 Oct 2026 15:00:30 GMT"), a date already past reading as 0. Absent,
 * empty or unparseable is null, so a caller never counts down from a wait nobody named.
 */
export function retryAfterSeconds(header, now = Date.now()) {
    const value = header?.trim();
    if (!value)
        return null;
    if (/^\d+$/.test(value))
        return Number(value);
    const at = Date.parse(value);
    return Number.isNaN(at) ? null : Math.max(0, Math.ceil((at - now) / 1000));
}
/** One header off an answer: a fetch `Headers` (ofetch) or a plain map (axios, which lowercases names). */
function headerOf(headers, name) {
    if (typeof headers !== 'object' || headers === null) {
        return null;
    }
    const value = typeof headers.get === 'function'
        ? headers.get(name)
        : headers[name];
    return typeof value === 'string' ? value : null;
}
/**
 * Read a thrown request failure: ofetch's FetchError (`response._data`, `data`), axios's error
 * (`response.data`), or an error thrown before anything reached the wire. `now` is the clock a
 * `Retry-After` date is read against.
 */
export function readFetchFailure(error, now = Date.now()) {
    const failure = (typeof error === 'object' && error !== null ? error : {});
    const status = [failure.response?.status, failure.status, failure.statusCode]
        .find((candidate) => typeof candidate === 'number' && candidate > 0) ?? null;
    const body = failure.response?._data ?? failure.response?.data ?? failure.data;
    const entry = envelopeEntry(body);
    const retryAfter = retryAfterSeconds(headerOf(failure.response?.headers, 'retry-after'), now);
    if (entry) {
        return { status, body, entry, message: entry.message ?? '', retryAfter };
    }
    const answered = body;
    const message = [answered?.error?.message, answered?.message, failure.message, failure.statusText]
        .map(nonEmpty)
        .find((candidate) => candidate !== null);
    return { status, body, entry: null, message: message ?? 'An error occurred', retryAfter };
}
/**
 * A failed call to app-api, as the apps' request clients raise it: the HTTP status, the envelope's
 * reason when the API sent one, and the wait a throttled answer named. Each app's own `ApiError`
 * extends it with what that app reads besides, so `memberFacingMessage` can hold every app to the
 * one rule.
 */
export class PbApiError extends Error {
    code;
    /** The envelope's machine-readable reason; never an empty string, null when none was sent. */
    reason;
    /** The answer's `Retry-After` in whole seconds (`FailureReading.retryAfter`); null when it named no wait. */
    retryAfter;
    /**
     * `message` is empty when the failure came with no sentence — an envelope that wrote none — so
     * a reader that shows it falls back on its own (`message || fallback`) rather than on a
     * stand-in, and a reason never vouches for words the API did not write.
     */
    constructor(message, code = 500, reason = null, retryAfter = null) {
        super(message);
        this.name = 'ApiError';
        this.code = code;
        this.reason = nonEmpty(reason);
        this.retryAfter = retryAfter;
    }
}
/**
 * The reason app-api gives a spent allowance on a partner or staff route, a 429. Its sentence is
 * the framework's "Too Many Attempts.", which tells a person neither what to do nor how long to
 * wait, so `memberFacingMessage` words that refusal itself (`tooManyRequestsMessage`).
 */
export const TOO_MANY_REQUESTS_REASON = 'too_many_requests';
/** A wait the API asked for, in the words a reader counts in; a minute where it did not say. */
export function waitInWords(seconds) {
    if (seconds === null)
        return 'a minute';
    if (seconds < 120)
        return seconds === 1 ? '1 second' : `${seconds} seconds`;
    return `${Math.ceil(seconds / 60)} minutes`;
}
/** What a person reads when a limiter refuses them: how long to wait (`Retry-After`), then to try again. */
export function tooManyRequestsMessage(retryAfter) {
    return `Too many attempts. Wait ${waitInWords(retryAfter)}, then try again.`;
}
/**
 * The one sentence to show a person when an API call fails: the API's own, only when a reason
 * proves it was written for them; otherwise the caller's. A spent allowance
 * (`TOO_MANY_REQUESTS_REASON`) is the one reason whose sentence is not, and reads as
 * `tooManyRequestsMessage` with the wait the answer named.
 *
 * Three kinds of failure can carry that proof: a `PbApiError` (each app's `ApiError`), which holds
 * the envelope's reason beside its sentence; a request failure thrown as it came off the wire
 * (ofetch's FetchError, axios's error); and a failed answer as a request client resolves one
 * (`{status, data, message}`, `data` being the body). The body of the last two is read here.
 * Anything else — a vendor's error, an error that merely has a `reason` property, a thrown string —
 * is not.
 */
export function memberFacingMessage(error, fallback) {
    if (error instanceof PbApiError) {
        if (error.reason === TOO_MANY_REQUESTS_REASON)
            return tooManyRequestsMessage(error.retryAfter);
        return error.reason !== null && error.message !== '' ? error.message : fallback;
    }
    const { entry, retryAfter } = readFetchFailure(error);
    if (entry?.reason === TOO_MANY_REQUESTS_REASON)
        return tooManyRequestsMessage(retryAfter);
    return entry?.reason && entry.message ? entry.message : fallback;
}
function nonEmpty(value) {
    return typeof value === 'string' && value !== '' ? value : null;
}
